/**
 * One source of truth for cookie consent, shared by every analytics tag.
 *
 * Before this existed, "Reject all" in the cookie banner only hid the banner —
 * Google Analytics, Clarity, LinkedIn and the Meta Pixel all carried on
 * tracking exactly as before. That is worse than having no banner at all,
 * because the banner promises the visitor something the site does not do.
 */

const CONSENT_KEY = "cookie_consent";

/** Dispatched on window when the visitor accepts or rejects, so live pages react. */
export const CONSENT_EVENT = "vilva:cookie-consent";

/**
 * Whether tracking is allowed right now.
 *
 * This is an OPT-OUT model, matching how the site already behaves: the banner
 * only appears after 40 seconds and every tag loads before then. Switching to
 * strict opt-in would silently discard the majority of analytics data, so the
 * rule is: track by default, and stop the moment someone explicitly rejects.
 *
 * "rejected" is deliberately session-scoped (sessionStorage) and "accepted"
 * persistent (localStorage) — that is the banner's existing behaviour.
 */
export function isTrackingAllowed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(CONSENT_KEY) !== "rejected";
  } catch {
    // Storage blocked (private mode) — there is no explicit rejection to honour.
    return true;
  }
}

/** True once the visitor has explicitly rejected. */
export function hasRejectedTracking(): boolean {
  return typeof window !== "undefined" && !isTrackingAllowed();
}

/** Notify every listening tag that consent changed. */
export function notifyConsentChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function onConsentChange(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CONSENT_EVENT, handler);
  return () => window.removeEventListener(CONSENT_EVENT, handler);
}
